import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Filter,
  Layers,
  Loader2,
  Package,
} from 'lucide-react';

import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { SearchIcon } from 'components/icons/Icons';
import { Checkbox } from 'components/forms/fragments/Checkbox';

import {
  CourtierMinimalForMatrixDto,
  GetHabilitationMatrixResponse,
  TunnelMinimalForMatrixDto,
  getHabilitationMatrix,
  setCourtierTunnelHabilitation,
} from '../api/habilitationsTunnelsApi';
import { getTunnelsAutorisesPourMia } from 'back-office/features/mias-view/api/miasApi';

const NATURE_MIA = 'MIA';

interface Props {
  plateformeId: string;
}

const TYPE_TUNNEL_LABELS: Record<number, string> = {
  1: 'Individuel',
  2: 'Collectif',
};

const UNKNOWN_PRODUCT_KEY = '__no_product__';

interface ProductGroup {
  produitId: string;
  produitLabel: string;
  produitCode?: string | null;
  tunnels: TunnelMinimalForMatrixDto[];
}

export const HabilitationsTunnelsManager: React.FC<Props> = ({ plateformeId }) => {
  const [data, setData] = useState<GetHabilitationMatrixResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTunnelId, setSelectedTunnelId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [courtierSearch, setCourtierSearch] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // Tunnels autorisés par MIA (union des habilitations actives de ses mandants).
  // La validation serveur du sous-ensemble reste systématique côté backend.
  const [miaTunnelsAutorises, setMiaTunnelsAutorises] = useState<Record<string, Set<string>>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getHabilitationMatrix(plateformeId);
      setData(response);

      const mias = response.courtiers.filter((c) => c.nature === NATURE_MIA);
      const entries = await Promise.all(
        mias.map(async (mia) => {
          try {
            const autorises = await getTunnelsAutorisesPourMia(plateformeId, mia.courtierId);
            return [mia.courtierId, new Set(autorises.tunnelIds)] as const;
          } catch {
            // En cas d'échec, aucune case cochable pour ce MIA (fail-safe)
            return [mia.courtierId, new Set<string>()] as const;
          }
        }),
      );
      setMiaTunnelsAutorises(Object.fromEntries(entries));
    } catch {
      toasterService.showToast({
        message: 'Erreur de chargement des habilitations.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setLoading(false);
    }
  }, [plateformeId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const productGroups = useMemo<ProductGroup[]>(() => {
    if (!data) return [];
    const map = new Map<string, ProductGroup>();
    for (const t of data.tunnels) {
      const key = t.produitId ?? UNKNOWN_PRODUCT_KEY;
      if (!map.has(key)) {
        map.set(key, {
          produitId: key,
          produitLabel: t.produitLabel ?? 'Sans produit',
          produitCode: t.produitCode,
          tunnels: [],
        });
      }
      map.get(key)!.tunnels.push(t);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.produitLabel.localeCompare(b.produitLabel, 'fr'),
    );
  }, [data]);

  const habilitationLookup = useMemo(() => {
    const map = new Map<string, boolean>();
    data?.habilitations.forEach((h) => {
      map.set(`${h.tunnelId}|${h.courtierId}`, h.isActive);
    });
    return map;
  }, [data]);

  const isHabilited = useCallback(
    (tunnelId: string, courtierId: string) =>
      habilitationLookup.get(`${tunnelId}|${courtierId}`) === true,
    [habilitationLookup],
  );

  /**
   * Un MIA ne peut être habilité que sur les tunnels couverts par les
   * habilitations actives de ses courtiers mandants.
   */
  const isTunnelSelectableFor = useCallback(
    (tunnelId: string, courtier: CourtierMinimalForMatrixDto) => {
      if (courtier.nature !== NATURE_MIA) return true;
      return miaTunnelsAutorises[courtier.courtierId]?.has(tunnelId) === true;
    },
    [miaTunnelsAutorises],
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectedTunnel = useMemo(
    () => data?.tunnels.find((t) => t.tunnelId === selectedTunnelId) ?? null,
    [data, selectedTunnelId],
  );

  const filteredCourtiers = useMemo<CourtierMinimalForMatrixDto[]>(() => {
    if (!data) return [];
    const term = courtierSearch.trim().toLowerCase();
    if (!term) return data.courtiers;
    return data.courtiers.filter(
      (c) =>
        c.designation.toLowerCase().includes(term) ||
        (c.numeroSiret ?? '').toLowerCase().includes(term),
    );
  }, [data, courtierSearch]);

  // Courtiers cochables pour le tunnel sélectionné (exclut les MIA hors
  // tunnels de leurs mandants — le "tout sélectionner" ne les touche pas)
  const selectableCourtiers = useMemo(() => {
    if (!selectedTunnelId) return filteredCourtiers;
    return filteredCourtiers.filter((c) => isTunnelSelectableFor(selectedTunnelId, c));
  }, [selectedTunnelId, filteredCourtiers, isTunnelSelectableFor]);

  const allFilteredHabilited = useMemo(() => {
    if (!selectedTunnelId || selectableCourtiers.length === 0) return false;
    return selectableCourtiers.every((c) => isHabilited(selectedTunnelId, c.courtierId));
  }, [selectedTunnelId, selectableCourtiers, isHabilited]);

  const habilitedCountForSelected = useMemo(() => {
    if (!data || !selectedTunnelId) return 0;
    return data.courtiers.filter((c) => isHabilited(selectedTunnelId, c.courtierId)).length;
  }, [data, selectedTunnelId, isHabilited]);

  const handleToggleOne = useCallback(
    async (tunnelId: string, courtierId: string, currentValue: boolean) => {
      const key = `${tunnelId}|${courtierId}`;
      setSavingKey(key);

      setData((prev) => {
        if (!prev) return prev;
        const existing = prev.habilitations.find(
          (h) => h.tunnelId === tunnelId && h.courtierId === courtierId,
        );
        const next = !currentValue;
        const updatedHabilitations = existing
          ? prev.habilitations.map((h) =>
              h.tunnelId === tunnelId && h.courtierId === courtierId
                ? { ...h, isActive: next }
                : h,
            )
          : [
              ...prev.habilitations,
              {
                tunnelId,
                courtierId,
                isActive: next,
                dateHabilitation: new Date().toISOString(),
              },
            ];
        return { ...prev, habilitations: updatedHabilitations };
      });

      const res = await setCourtierTunnelHabilitation(
        plateformeId,
        tunnelId,
        courtierId,
        !currentValue,
      );

      if (!res.isSuccess) {
        toasterService.showToast({
          message: res.errorMessage ?? 'Échec de la mise à jour',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        await refresh();
      }
      setSavingKey(null);
    },
    [plateformeId, refresh],
  );

  const handleToggleAll = useCallback(async () => {
    if (!selectedTunnelId || selectableCourtiers.length === 0) return;
    const target = !allFilteredHabilited;
    setSavingKey('all');

    setData((prev) => {
      if (!prev) return prev;
      const map = new Map(
        prev.habilitations.map((h) => [`${h.tunnelId}|${h.courtierId}`, h]),
      );
      for (const c of selectableCourtiers) {
        const key = `${selectedTunnelId}|${c.courtierId}`;
        const existing = map.get(key);
        if (existing) {
          map.set(key, { ...existing, isActive: target });
        } else {
          map.set(key, {
            tunnelId: selectedTunnelId,
            courtierId: c.courtierId,
            isActive: target,
            dateHabilitation: new Date().toISOString(),
          });
        }
      }
      return { ...prev, habilitations: Array.from(map.values()) };
    });

    let hasError = false;
    for (const c of selectableCourtiers) {
      const current = isHabilited(selectedTunnelId, c.courtierId);
      if (current === target) continue;
      const res = await setCourtierTunnelHabilitation(
        plateformeId,
        selectedTunnelId,
        c.courtierId,
        target,
      );
      if (!res.isSuccess) {
        hasError = true;
        break;
      }
    }

    if (hasError) {
      toasterService.showToast({
        message: 'Au moins une mise à jour a échoué',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
      await refresh();
    } else {
      toasterService.showToast({
        message: target
          ? `${selectableCourtiers.length} courtiers habilités`
          : `${selectableCourtiers.length} courtiers retirés`,
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green,
      });
    }
    setSavingKey(null);
  }, [
    selectedTunnelId,
    selectableCourtiers,
    allFilteredHabilited,
    plateformeId,
    isHabilited,
    refresh,
  ]);

  if (loading && !data) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  if (!data || data.tunnels.length === 0) {
    return (
      <div>
        <h2 className="text-3xl font-semibold text-zinc-800 flex items-center gap-3 mb-4">
          <Filter className="text-primary w-7 h-7" />
          Habilitations tunnels
        </h2>
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Layers className="mx-auto text-gray-300 mb-2" size={32} />
          <p className="text-sm text-gray-500">
            Aucun tunnel rattaché à cette plateforme.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Rattachez d&apos;abord des tunnels via la gestion des plateformes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-semibold text-zinc-800 flex items-center gap-3 mb-4">
        <Filter className="text-primary w-7 h-7" />
        Habilitations tunnels
      </h2>

      <div className="grid grid-cols-12 gap-4">
        {/* Liste des tunnels par produit */}
        <div className="col-span-5 bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="bg-primary px-6 py-3 flex items-center gap-2">
            <Package className="text-white" size={16} />
            <span className="text-xs font-medium text-white tracking-wider uppercase">
              Tunnels · {data.tunnels.length}
            </span>
          </div>

          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-200">
            {productGroups.map((group) => {
              const isCollapsed = collapsedGroups.has(group.produitId);
              return (
                <div key={group.produitId}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.produitId)}
                    className="w-full flex items-center gap-2 px-6 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    {isCollapsed ? (
                      <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-sm font-semibold text-gray-900 truncate flex-1">
                      {group.produitLabel}
                    </span>
                    <span className="text-xs text-gray-400">
                      {group.tunnels.length}
                    </span>
                  </button>
                  {!isCollapsed && (
                    <ul className="divide-y divide-gray-200">
                      {group.tunnels.map((t) => {
                        const isSelected = selectedTunnelId === t.tunnelId;
                        return (
                          <li key={t.tunnelId}>
                            <button
                              type="button"
                              onClick={() => setSelectedTunnelId(t.tunnelId)}
                              className={`w-full flex items-center gap-3 px-6 py-3 text-left hover:bg-gray-50 transition-colors ${
                                isSelected ? 'bg-primary/5' : ''
                              }`}
                            >
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Layers className="text-primary" size={14} />
                              </div>
                              <div className="flex flex-col min-w-0 flex-1">
                                <span
                                  className={`text-sm font-medium truncate ${
                                    isSelected ? 'text-primary' : 'text-gray-900'
                                  }`}
                                >
                                  {t.designation || t.code}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {t.code} · {TYPE_TUNNEL_LABELS[t.typeTunnel] ?? '—'}
                                </span>
                              </div>
                              {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Détail tunnel sélectionné — courtiers */}
        <div className="col-span-7 flex flex-col gap-4">
          {selectedTunnel ? (
            <>
              <div className="flex items-center justify-between">
                <div className="relative w-64">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                    <SearchIcon className="h-3 w-3 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un courtier..."
                    value={courtierSearch}
                    onChange={(e) => setCourtierSearch(e.target.value)}
                    className="w-full py-2 pl-8 pr-3 text-xs rounded-xl border bg-white leading-5 text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-25"
                  />
                </div>

                <div className="inline-flex items-center gap-2 text-xs text-gray-700 whitespace-nowrap">
                  <Checkbox
                    checked={allFilteredHabilited}
                    disabled={savingKey === 'all' || filteredCourtiers.length === 0}
                    onChange={handleToggleAll}
                  />
                  <span
                    className={
                      savingKey === 'all' || filteredCourtiers.length === 0
                        ? 'opacity-50'
                        : 'cursor-pointer'
                    }
                    onClick={() => {
                      if (savingKey === 'all' || filteredCourtiers.length === 0) return;
                      handleToggleAll();
                    }}
                  >
                    Tout sélectionner
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-primary px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="text-white flex-shrink-0" size={16} />
                    <span className="text-xs font-medium text-white tracking-wider uppercase truncate">
                      {selectedTunnel.designation || selectedTunnel.code}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-white/90 whitespace-nowrap">
                    {habilitedCountForSelected} / {data.courtiers.length} habilités
                  </span>
                </div>

                <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-200">
                  {filteredCourtiers.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      Aucun courtier ne correspond à la recherche.
                    </div>
                  ) : (
                    filteredCourtiers.map((c) => {
                      const key = `${selectedTunnel.tunnelId}|${c.courtierId}`;
                      const checked = isHabilited(selectedTunnel.tunnelId, c.courtierId);
                      const isMia = c.nature === NATURE_MIA;
                      // Un MIA ne peut être coché que sur les tunnels de ses mandants
                      // (une habilitation existante hors périmètre reste décochable)
                      const selectable =
                        isTunnelSelectableFor(selectedTunnel.tunnelId, c) || checked;
                      const isSaving = savingKey === key || savingKey === 'all';
                      const toggle = () => {
                        if (isSaving || !selectable) return;
                        handleToggleOne(selectedTunnel.tunnelId, c.courtierId, checked);
                      };
                      return (
                        <div
                          key={c.courtierId}
                          role="button"
                          tabIndex={selectable ? 0 : -1}
                          aria-disabled={!selectable}
                          onClick={toggle}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggle();
                            }
                          }}
                          title={
                            !selectable
                              ? 'Tunnel hors habilitations des courtiers mandants de ce MIA'
                              : undefined
                          }
                          className={`flex items-center gap-3 px-6 py-4 transition-colors ${
                            !selectable
                              ? 'opacity-50 cursor-not-allowed'
                              : isSaving
                                ? 'cursor-wait hover:bg-gray-50'
                                : 'cursor-pointer hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                            <Building2 className="text-purple-600" size={16} />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {c.designation}
                              {isMia && (
                                <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-[10px] font-semibold align-middle">
                                  MIA
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-gray-500">
                              SIRET: {c.numeroSiret || '—'}
                            </span>
                          </div>
                          <Checkbox
                            checked={checked}
                            disabled={isSaving || !selectable}
                            onChange={toggle}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 flex flex-col items-center justify-center text-center p-12 min-h-[300px] text-gray-500">
              <Filter className="text-gray-300 mb-3" size={36} />
              <p className="text-sm font-medium">Sélectionnez un tunnel</p>
              <p className="text-xs text-gray-400 mt-1">
                Choisissez un tunnel dans la liste à gauche pour gérer ses
                courtiers habilités.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
